import { Sprite } from "@pixi/react";
import type { ColorOverlayFilter } from "@pixi/filter-color-overlay";
import { PlaceCircleRingWhite, PlaceCircleWhite } from "./SpriteIndex";

/** Occupancy fill/ring SVGs: viewBox 120 with disk `r=50` (painted diameter 100). */
export const PLACE_CIRCLE_SVG_VIEWBOX = 120;
/** Painted disk diameter in SVG units for the white fill / ring assets. */
export const PLACE_CIRCLE_SVG_PAINTED_DIAMETER = 100;

/** Sprite size so the painted disk matches a Graphics circle of `paintedDiameter`. */
export function occupancyCircleSpriteSize(paintedDiameter: number): number {
  return (paintedDiameter * PLACE_CIRCLE_SVG_VIEWBOX) / PLACE_CIRCLE_SVG_PAINTED_DIAMETER;
}

/** Shared Pixi sprite props for the tinted place fill / circle path (non-Firefox). */
export type TintedPlaceSpriteProps = {
  /** Testing Library id on the Pixi Sprite wrapper */
  testId: string;
  /** Pixi tint (hex), e.g. highlight khaki or report overlay */
  tint: number;
  width: number;
  height: number;
  x: number;
  y: number;
  isSelectable: boolean;
  filterGroup: ColorOverlayFilter[];
};

function pointerCursor(isSelectable: boolean) {
  return isSelectable ? "pointer" : "auto";
}

export function TintedPlaceRectSprite({
  testId,
  image,
  tint,
  width,
  height,
  x,
  y,
  isSelectable,
  filterGroup
}: TintedPlaceSpriteProps & { image: string }) {
  return (
    <Sprite
      data-testid={testId}
      image={image}
      tint={tint}
      position={{ x, y }}
      width={width}
      height={height}
      filters={filterGroup}
      cursor={pointerCursor(isSelectable)}
    />
  );
}

export function TintedPlaceCircleSprite({
  testId,
  tint,
  diameter,
  x,
  y,
  isSelectable,
  filterGroup
}: Omit<TintedPlaceSpriteProps, "width" | "height"> & { diameter: number }) {
  const cursor = pointerCursor(isSelectable);
  const position = { x, y };
  const size = occupancyCircleSpriteSize(diameter);

  return (
    <>
      <Sprite
        data-testid={testId}
        image={PlaceCircleWhite}
        tint={tint}
        anchor={0.5}
        width={size}
        height={size}
        position={position}
        filters={filterGroup}
        cursor={cursor}
      />
      <Sprite
        data-testid={`${testId}-ring`}
        image={PlaceCircleRingWhite}
        tint={0xffffff}
        anchor={0.5}
        width={size}
        height={size}
        position={position}
        filters={filterGroup}
        cursor={cursor}
      />
    </>
  );
}
